// Pure currency helpers — no React, no data fetching. Safe to import from
// server components and the public quote themes as well as from the
// `use-currency` hook (which re-exports these for existing call sites).
//
// Mirrors the backend App\Support\Money.

export const DEFAULT_CURRENCY = "EUR"

/** Locale + precision per currency. Mirrors Money::decimals(). */
export const CURRENCY_CONFIG: Record<string, { locale: string; decimals: number }> = {
  USD: { locale: "en-US", decimals: 2 },
  EUR: { locale: "de-DE", decimals: 2 },
  GBP: { locale: "en-GB", decimals: 2 },
  ALL: { locale: "sq-AL", decimals: 0 },
  TRY: { locale: "tr-TR", decimals: 2 },
  CAD: { locale: "en-CA", decimals: 2 },
  AUD: { locale: "en-AU", decimals: 2 },
  JPY: { locale: "ja-JP", decimals: 0 },
  CNY: { locale: "zh-CN", decimals: 2 },
  MXN: { locale: "es-MX", decimals: 2 },
}

/** Display symbols. Mirrors Money::symbol(). */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  ALL: "L",
  TRY: "₺",
  CAD: "CA$",
  AUD: "A$",
  JPY: "¥",
  CNY: "¥",
  MXN: "MX$",
}

/**
 * Symbol for a currency code; unknown codes fall back to the code itself so
 * nothing renders blank. Use this instead of embedding local symbol maps.
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency
}

/** Decimal precision for a currency (ALL and JPY are whole-unit). */
export function getCurrencyDecimals(currency: string): number {
  return CURRENCY_CONFIG[currency]?.decimals ?? 2
}

/**
 * Group amounts by currency and sum each group. Tier 1 multi-currency never
 * converts, so any total over mixed-currency rows is a LIST of per-currency
 * totals, never one number. Generalized from the payroll register, which has
 * done this since staff could be paid in ALL or EUR.
 *
 * Returns groups in first-seen order; single-currency data yields one group.
 */
export function groupSumByCurrency<T>(
  items: readonly T[],
  getCurrency: (item: T) => string | null | undefined,
  getAmount: (item: T) => number,
  fallbackCurrency: string = DEFAULT_CURRENCY,
): { currency: string; total: number }[] {
  const totals = new Map<string, number>()

  for (const item of items) {
    const currency = getCurrency(item) || fallbackCurrency
    totals.set(currency, (totals.get(currency) ?? 0) + (getAmount(item) || 0))
  }

  return Array.from(totals, ([currency, total]) => ({ currency, total }))
}
